import { useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { selectTacho } from "@/reducers/tachosSliceSelectors"
import { selectDriverParticipant } from "@/reducers/chatsSliceSelectors"
import { Tacho } from "@/reducers/tachosSlice"
import { isObuRoomGroup } from "@/lib/slickAISilence"

export type TachoWithDriver = {
  tacho: Tacho
}

const CREW_TACHO_MAX_AGE_MS = 60 * 60 * 1000
// GPS republishes every driver's counters every few minutes. A single-driver
// tacho hours old means GPS stopped delivering for that driver (mapping
// dropped, provider outage) and the batch node still carries an old
// snapshot - beedee.eu showed KK5205U the counters of 15.08 on 16.09.
// Showing nothing beats showing that as "now".
const SINGLE_TACHO_MAX_AGE_MS = 3 * 60 * 60 * 1000

// Tachos without a timestamp predate the GPS-side stamping - treat as fresh.
const isTachoFresh = (
  tacho: Tacho | undefined,
  maxAgeMs: number,
): tacho is Tacho => {
  if (!tacho) return false
  if (!tacho.timestamp) return true
  const publishedAt = new Date(tacho.timestamp).getTime()
  if (Number.isNaN(publishedAt)) return true
  return Date.now() - publishedAt < maxAgeMs
}

export const useChatTachos = (chatJid: string) => {
  const accountChatSelector = useMemo(
    () => selectAccountChatByJid(chatJid),
    [chatJid],
  )
  const accountChat = useAppSelector(accountChatSelector)

  const driverParticipantSelector = useMemo(
    () => selectDriverParticipant(chatJid),
    [chatJid],
  )
  const driverParticipant = useAppSelector(driverParticipantSelector)

  const isExplicitDoubleCrew = Boolean(
    accountChat?.driverA && accountChat?.driverB,
  )

  // Single driver: prefer driverA from accountChat, fallback to driver participant
  const singleDriverJid = accountChat?.driverA ?? driverParticipant?.jid
  const selectDriverTacho = useMemo(
    () => selectTacho(singleDriverJid),
    [singleDriverJid],
  )
  const storedSingleTacho = useAppSelector(selectDriverTacho)
  const singleTacho = isTachoFresh(storedSingleTacho, SINGLE_TACHO_MAX_AGE_MS)
    ? storedSingleTacho
    : undefined

  // A tacho keyed by the chat JID itself is a merged double-crew tacho. GPS
  // publishes it both for explicit double_crew chats and for vehicles the
  // telematics provider reports as multi-manned (two drivers on one device),
  // so its presence alone means the vehicle currently runs a double crew.
  // A stale one means the crew dissolved (GPS stopped refreshing it) - fall
  // back to the individual driver tachos then.
  const selectChatJidTacho = useMemo(() => selectTacho(chatJid), [chatJid])
  const storedChatJidTacho = useAppSelector(selectChatJidTacho)
  const tachoForChatJid = isTachoFresh(
    storedChatJidTacho,
    CREW_TACHO_MAX_AGE_MS,
  )
    ? storedChatJidTacho
    : undefined

  // OBU-bridged rooms always get a chat-JID-keyed tacho (published by GPS
  // under the room JID) - usually a single driver behind the on-board unit,
  // a crew only when GPS explicitly marks the entry as double_crew (both
  // cards reported on the vehicle, e.g. Transics Driver + CoDriver).
  const isObuChat = isObuRoomGroup(accountChat?.group)
  const isDoubleCrew =
    isExplicitDoubleCrew ||
    (Boolean(tachoForChatJid) &&
      (!isObuChat || Boolean(tachoForChatJid?.doubleCrew)))

  // Double crew driver A
  const selectDriverATacho = useMemo(
    () => selectTacho(accountChat?.driverA),
    [accountChat?.driverA],
  )
  const tachoDriverA = useAppSelector(selectDriverATacho)

  // Double crew driver B
  const selectDriverBTacho = useMemo(
    () => selectTacho(accountChat?.driverB),
    [accountChat?.driverB],
  )
  const tachoDriverB = useAppSelector(selectDriverBTacho)

  const tachos: TachoWithDriver[] = useMemo(() => {
    // A merged crew tacho published under the chat JID always wins
    if (tachoForChatJid) {
      return [{ tacho: tachoForChatJid }]
    }

    if (isExplicitDoubleCrew) {
      // Fall back to individual driver tachos
      const result: TachoWithDriver[] = []
      if (tachoDriverA) {
        result.push({ tacho: tachoDriverA })
      }
      if (tachoDriverB) {
        result.push({ tacho: tachoDriverB })
      }
      return result
    }

    if (singleTacho) {
      return [{ tacho: singleTacho }]
    }

    return []
  }, [
    isExplicitDoubleCrew,
    singleTacho,
    tachoForChatJid,
    tachoDriverA,
    tachoDriverB,
  ])

  return { tachos, isDoubleCrew }
}
